import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LuArrowRight,
  LuCalendarCheck,
  LuCalendarDays,
  LuContact,
  LuFiles,
  LuGraduationCap,
  LuReceiptIndianRupee,
  LuSparkles,
  LuUsers,
} from 'react-icons/lu';
import StatsCard from './components/StatsCard';
import Card from '../common/Card';
import useStudentStore from '../../store/studentStore';
import useSchoolStore from '../../store/schoolStore';
import useStaffStore from '../../store/staffStore';
import useUserStore from '../../store/userStore';
import { isFeatureEnabled } from '../../config/appConfig';
import { STRINGS } from '../../constants/strings';

// Ready-made questions shown on Home. Clicking one opens Ask Me with its form (AskMePage reads location.state.commandId).
const homeCommandIds = ['attendance', 'class-attendance', 'subject-performance'];

const greetingForHour = (hour) => {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

/**
 * The home screen: who is signed in, the school's headline numbers, Ask Me and shortcuts
 * to the jobs people come here for. Only real numbers from the stores are shown.
 */
const DashboardHome = () => {
  const navigate = useNavigate();
  const { schoolId, schoolData } = useSchoolStore();
  const { userProfile } = useUserStore();

  const {
    totalNumberOfActiveStudents,
    isTotalNumberOfActiveStudentsLoading,
    totalNumberOfActiveStudentsError,
    getTotalNumberOfActiveStudents
  } = useStudentStore();

  const {
    totalNumberOfActiveStaff,
    isTotalNumberOfActiveStaffLoading,
    totalNumberOfActiveStaffError,
    getTotalNumberOfActiveStaff
  } = useStaffStore();

  useEffect(() => {
    if (schoolId) {
      getTotalNumberOfActiveStudents(schoolId);
      getTotalNumberOfActiveStaff(schoolId);
    }
  }, [getTotalNumberOfActiveStudents, getTotalNumberOfActiveStaff, schoolId]);

  const base = `/dash/${schoolId}`;
  const firstName = (userProfile?.name || '').split(' ')[0];
  const today = new Date();
  const todayLabel = today.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const shortcuts = [
    { label: 'Look up a student', icon: LuUsers, to: `${base}/students`, isShown: true },
    { label: 'Check class attendance', icon: LuCalendarCheck, to: `${base}/attendance`, isShown: true },
    { label: 'See fee payments and dues', icon: LuReceiptIndianRupee, to: `${base}/fees`, isShown: true },
    { label: 'Open a student’s report', icon: LuGraduationCap, to: `${base}/student-report`, isShown: true },
    { label: 'Make reports for a whole class', icon: LuFiles, to: `${base}/bulk-reports`, isShown: isFeatureEnabled('bulkReports') },
  ].filter((shortcut) => shortcut.isShown);

  const homeCommands = STRINGS.ASKME.COMMANDS.filter((command) => homeCommandIds.includes(command.id));

  const handlePickCommand = (command) => {
    navigate(`${base}/ask`, { state: { commandId: command.id } });
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-ui-ink">
          {greetingForHour(today.getHours())}{firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="mt-1 text-sm text-ui-ink-2">{todayLabel}</p>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatsCard
          title="Students"
          caption="Active students"
          value={totalNumberOfActiveStudents?.toLocaleString('en-IN')}
          icon={<LuUsers />}
          isLoading={isTotalNumberOfActiveStudentsLoading}
          error={totalNumberOfActiveStudentsError}
          onClick={() => navigate(`${base}/students`)}
        />
        <StatsCard
          title="Staff"
          caption="Active staff members"
          value={totalNumberOfActiveStaff?.toLocaleString('en-IN')}
          icon={<LuContact />}
          isLoading={isTotalNumberOfActiveStaffLoading}
          error={totalNumberOfActiveStaffError}
          onClick={() => navigate(`${base}/staff`)}
        />
        <StatsCard
          title="Academic year"
          caption={schoolData?.name || 'Current academic year'}
          value={schoolData?.currentAcademicYear}
          icon={<LuCalendarDays />}
          isLoading={!schoolData}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2" bodyClassName="flex h-full flex-col">
          <div className="flex items-start gap-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-ui-brand-soft text-brand-600 dark:text-brand-300">
              <LuSparkles aria-hidden className="size-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-[15px] font-semibold text-ui-ink">Ask Me</h2>
              <p className="mt-1 text-sm leading-6 text-ui-ink-2">
                Ask about students, classes, attendance or exam results in plain words, using the box at the top of
                every page. Or start with a ready-made question:
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2">
            {homeCommands.map((command) => (
              <button
                key={command.id}
                type="button"
                onClick={() => handlePickCommand(command)}
                className="group flex items-center justify-between gap-3 rounded-lg border border-ui-line px-4 py-2.5 text-left transition-colors hover:border-brand-300 hover:bg-ui-brand-soft dark:hover:border-brand-500/40"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-ui-ink">{command.title}</span>
                  <span className="block truncate text-[13px] text-ui-ink-2">{command.description}</span>
                </span>
                <LuArrowRight aria-hidden className="size-4 shrink-0 text-ui-ink-3 transition-transform group-hover:translate-x-0.5 group-hover:text-brand-600" />
              </button>
            ))}
          </div>

          <Link
            to={`${base}/ask`}
            className="mt-4 self-start text-sm font-medium text-ui-link hover:underline"
          >
            See all ready-made questions
          </Link>
        </Card>

        <Card title="Shortcuts" noPadding>
          <ul className="p-2">
            {shortcuts.map((shortcut) => {
              const Icon = shortcut.icon;
              return (
                <li key={shortcut.to}>
                  <Link
                    to={shortcut.to}
                    className="group flex h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium text-ui-ink transition-colors hover:bg-ui-hover"
                  >
                    <Icon aria-hidden className="size-[18px] shrink-0 text-ui-ink-3 group-hover:text-brand-600" />
                    <span className="flex-1">{shortcut.label}</span>
                    <LuArrowRight aria-hidden className="size-4 text-ui-ink-3 opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default DashboardHome;
